/**
 * Flujos.tsx — Página principal del módulo /flujos (reemplaza /gastos).
 *
 * Round 2026-05-13 — el módulo /gastos legacy nombraba mal el dominio: la
 * tabla `gastos` registra TODOS los movimientos de caja de AF Empresa, no
 * solo egresos. El nuevo módulo /flujos:
 *
 *   - Refleja los 3 sentidos del flujo (gasto / ingreso / intermediario)
 *     con 3 botones de creación pre-configurados (sin pedir al admin que
 *     elija `rol_de_aloja_facil` manualmente).
 *   - Agrupa los movimientos en 4 tabs (Todos / Ingresos / Gastos /
 *     Intermediarios) para facilitar el seguimiento.
 *   - Columna Frecuencia (Regular / Esporádico) en la tabla en vez de
 *     sub-tabs anidados.
 *
 * Sin cambios en backend ni en la DB. El campo `rol_de_aloja_facil` ∈
 * {paga, recibe, intermediario} sigue siendo la fuente de verdad del
 * tipo del flujo en `Expense`.
 *
 * Ver docs/flujo-financiero.md §10 y docs/modulo-finanzas-empresa.md.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useSidebar } from "../../hooks/useSidebar";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import { getMsg } from "../../utils/antdStatic";
import { apiFetch } from "../../services/apiClient";
import IntermediacionesLavanderia from "./IntermediacionesLavanderia";
import { propiedadesApi } from "../../services/propiedades.api";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { ESTADO_VIGENCIA_TAG, estadoVigencia } from "../../utils/vigenciaRegular";
import {
  FiltroGlobalFecha,
  useFiltroFechaPersistido,
} from "../finanzas-empresa/filtroGlobalFecha";
import FlujoFormModal from "./FlujoFormModal";
import AdjuntosEnFila from "./components/AdjuntosEnFila";
import {
  type Flujo,
  type FlujoPair,
  type FlujoParty,
  type FlujoPresetMode,
  CATEGORIA_LABEL,
  eliminarFlujo,
  frecuenciaToBadge,
  listarFlujos,
  pagoEntidadLabel,
  rolToTipoFlujo,
} from "./flujos.api";
import { ahoraChile } from "../../utils/fechaChile";
import { paginacionCliente } from "../../utils/tablaPaginacion";

dayjs.locale("es");

// ── Tipos de soporte ──────────────────────────────────────────────────────

interface UsuarioFinanzas {
  id: number;
  rol: string;
  nombre: string;
  propiedades?: string[];
}

interface PropiedadOpt {
  id: number;
  nombre: string;
}

type TabKey = "todos" | "ingresos" | "gastos" | "intermediarios";

/** Las únicas pestañas válidas hoy (para validar la que se recuerda). */
const TABS_VALIDAS: TabKey[] = ["todos", "ingresos", "gastos", "intermediarios"];

// ── Helpers de formato ─────────────────────────────────────────────────────

const clp = (v: number | null | undefined) =>
  v != null
    ? new Intl.NumberFormat("es-CL", {
        style: "currency",
        currency: "CLP",
        maximumFractionDigits: 0,
      }).format(v)
    : "—";

/**
 * Renderiza la celda "Pagado por" / "Pagado a" priorizando la PROPIEDAD
 * cuando la contraparte es cliente (tipo === 1):
 *   • Si hay `propiedadId` resuelto y la contraparte es cliente →
 *     muestra el nombre de la propiedad como label principal y el
 *     nombre del cliente en texto más chico debajo (con tooltip
 *     "Cliente: NOMBRE").
 *   • Si NO es cliente (AF, arrendatario, aseador, trabajador,
 *     receptor externo) → muestra el nombre tal como viene de
 *     `pagoEntidadLabel` (con comillas legacy strippeadas).
 *
 * Round 2026-06-01 — pedido del owner: contablemente lo importante de
 * un flujo asociado a un cliente es la propiedad afectada, no el humano.
 * Si Miguel gestiona 3 deptos y le pago algo sobre CHAGUAL 403B, quiero
 * ver "CHAGUAL 403B" en la grilla, no "Miguel Borges". El humano queda en
 * tooltip para auditoría. (Round 2026-06-20: el humano es el CLIENTE-gestor,
 * no el dueño legal — regla canónica flujo-financiero.md §10.3.1.)
 */
function renderContraparte(
  party: FlujoParty | null,
  propiedadId: number | null | undefined,
  propiedadesById: Map<number, PropiedadOpt>,
) {
  const fallback = pagoEntidadLabel(party);
  if (!party) return fallback;
  if (Number(party.tipo) !== 1) return fallback;
  const prop = propiedadId != null ? propiedadesById.get(propiedadId) : null;
  if (!prop) return fallback;
  return (
    <Tooltip title={`Cliente: ${fallback}`}>
      <div style={{ lineHeight: 1.2 }}>
        <div style={{ fontWeight: 500 }}>{formatPropertyName(prop.nombre)}</div>
        <div style={{ fontSize: 11, color: "#6b7280" }}>{fallback}</div>
      </div>
    </Tooltip>
  );
}

// ── Componente principal ───────────────────────────────────────────────────

export default function FlujosPage() {
  const { sidebarOpen, toggleSidebar } = useSidebar();

  // Catálogos (usuarios + propiedades) compartidos con /finanzas-empresa.
  const [usuarios, setUsuarios] = useState<UsuarioFinanzas[]>([]);
  const [propiedades, setPropiedades] = useState<PropiedadOpt[]>([]);

  // Data principal del módulo.
  const [flujos, setFlujos] = useState<Flujo[]>([]);
  const [loading, setLoading] = useState(false);

  // UI state.
  // La pestaña se recuerda: es un filtro más (parte la tabla en 4 mundos).
  // `validar` acota a las 4 keys de hoy para que una guardada de una versión
  // vieja no deje la página en una pestaña que ya no existe.
  const [tab, setTab] = useFiltroPersistido<TabKey>("flujos.pestana", "todos", {
    validar: (v) => TABS_VALIDAS.includes(v as TabKey),
  });
  // Filtro de fecha — mismo componente que /finanzas-empresa.
  // Soporta 3 modos: "mes" (MonthPicker), "rango" (RangePicker) y
  // "anterior" (atajo "Ver mes anterior").
  const [filtroFecha, setFiltroFecha] = useFiltroFechaPersistido(
    "flujos.periodo",
    { modo: "mes", mes: ahoraChile() },
  );

  // Calcula el rango [from, to] efectivo a partir del filtro.
  const [from, to] = useMemo<[Dayjs, Dayjs]>(() => {
    if (filtroFecha.modo === "rango" && filtroFecha.rango) {
      return [
        filtroFecha.rango[0].startOf("day"),
        filtroFecha.rango[1].endOf("day"),
      ];
    }
    const m = filtroFecha.mes ?? ahoraChile();
    return [m.startOf("month"), m.endOf("month")];
  }, [filtroFecha]);

  // Modal state.
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<FlujoPresetMode>("gasto");
  const [editingFlujo, setEditingFlujo] = useState<Flujo | null>(null);
  // Row par del editing cuando es un intermediario (se pasa al modal para
  // hidratar correctamente las dos puntas externas en la edición).
  const [editingPair, setEditingPair] = useState<Flujo | null>(null);

  // ─── Mejoras visuales round 2026-06-02 ─────────────────────────────────
  // Search global sobre los flujos visibles. Normalizamos sin acentos +
  // case-insensitive para que escribir "garcia" matchee "García".
  const [searchQuery, setSearchQuery] = useFiltroPersistido(
    "flujos.busqueda",
    "",
    { validar: (v) => typeof v === "string" },
  );
  // Highlight de la fila par al hacer hover sobre cualquier row con
  // `pairedId`. Se guarda el id del row hovered; cualquier fila cuyo
  // id O pairedId coincida pinta con fondo amarillo claro.
  const [hoveredPairAnchor, setHoveredPairAnchor] = useState<string | null>(
    null,
  );

  // ─── Carga inicial ────────────────────────────────────────────────────
  const fetchCatalogos = useCallback(async () => {
    const [users, props] = await Promise.all([
      apiFetch<UsuarioFinanzas[]>("/users/finanzas"),
      propiedadesApi.getAll(),
    ]);
    setUsuarios(Array.isArray(users) ? users : []);
    const propsRaw: any[] = Array.isArray(props?.data) ? props!.data : [];
    setPropiedades(
      propsRaw.map((p) => ({ id: p.id, nombre: p.nombrepropiedad })),
    );
  }, []);

  const fetchFlujos = useCallback(async () => {
    setLoading(true);
    try {
      // Enviamos el rango al backend para que filtre por fecha
      // (esporádicos) y permita que los regulares pasen sin recortarse.
      const rows = await listarFlujos(
        from.format("YYYY-MM-DD"),
        to.format("YYYY-MM-DD"),
      );
      setFlujos(rows);
    } catch (err: any) {
      getMsg()?.error(err?.message ?? "Error al cargar los flujos");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => {
    fetchCatalogos();
  }, [fetchCatalogos]);

  // Refetch cuando cambia el rango.
  useEffect(() => {
    fetchFlujos();
  }, [fetchFlujos]);

  // ─── Helpers de clasificación ─────────────────────────────────────────
  //
  // Round 2026-05-13 (iter B) — los intermediarios son 2 rows reales,
  // pero conceptualmente el usuario los entiende como "un intermediario".
  // Por eso los excluimos de las tabs Ingresos y Gastos (esas son para
  // flujos SUELTOS, sin par) y los mostramos solo en Todos e
  // Intermediarios.
  //
  // Casos:
  //   • rol='recibe' && pairedId == null  → Ingreso suelto.
  //   • rol='paga'   && pairedId == null  → Gasto suelto.
  //   • pairedId != null                   → Mitad de un intermediario.
  //   • rol='intermediario' (legacy 1-row) → Intermediario legacy.

  const esIngresoSuelto = (f: Flujo) =>
    f.rolDeAlojaFacil === "recibe" && f.pairedId == null;
  const esGastoSuelto = (f: Flujo) =>
    f.rolDeAlojaFacil === "paga" && f.pairedId == null;

  // Mapa de id → row para resolver el par rápido. Las claves son NÚMEROS
  // (igual que `id` y `pairedId` en runtime): antes se llenaba con el `id`
  // crudo —número— y se consultaba con `String(pairedId)`, así que todas las
  // búsquedas del par fallaban en silencio.
  const flujosById = useMemo(() => {
    const m = new Map<number, Flujo>();
    for (const f of flujos) m.set(Number(f.id), f);
    return m;
  }, [flujos]);

  // El estado de vigencia se calcula en `utils/vigenciaRegular.ts`: la misma
  // fila de `gastos` se muestra acá y en /sueldos, y con dos copias del cálculo
  // un mismo registro podría decir "Vigente" en una pantalla y "Terminado" en
  // la otra. Round 2026-07-28.

  /**
   * Clave canónica del PAR al que pertenece un flujo: el MENOR de los dos ids.
   * Ambas mitades la calculan igual, así que siempre coinciden.
   *
   * Antes la clave dependía del rol (`id` propio si 'recibe', `pairedId` si
   * no). Eso tenía dos fallas: mezclaba tipos (número en una rama, string en
   * la otra) y se rompía con cualquier par cuyos roles no fueran exactamente
   * 'recibe'/'paga' (por ejemplo los pares legacy con ambos lados
   * 'intermediario'). Usar el mínimo no depende del rol ni del tipo.
   */
  const pairKeyOf = (f: Flujo): string | null => {
    if (f.pairedId == null) return null;
    const a = Number(f.id);
    const b = Number(f.pairedId);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    return String(Math.min(a, b));
  };

  // Mapa de id → propiedad para resolver el nombre de la propiedad
  // asociada a un flujo en O(1) desde el render de la columna
  // "Pagado por" / "Pagado a". Lo usamos para mostrar la PROPIEDAD
  // como label primario cuando la contraparte es propietario (tipo=1)
  // — eso es lo que importa contablemente.
  const propiedadesById = useMemo(() => {
    const m = new Map<number, PropiedadOpt>();
    for (const p of propiedades) m.set(p.id, p);
    return m;
  }, [propiedades]);

  // ─── Identificador visual estable por PAR de intermediario ────────────
  //
  // Round 2026-06-05 — pedido del owner: cuando hay varios intermediarios
  // en la tab "Todos", sus 2 mitades (ingreso + gasto) aparecían todas con
  // el mismo badge 🔗 morado, sin forma de saber cuál va con cuál salvo
  // haciendo hover. Ahora cada PAR recibe un **número + color** estables,
  // que comparten sus dos mitades. Mismo número/color = mismo par.
  //
  // El "canonical id" del par es el id del lado 'recibe' (su representante);
  // ambas mitades resuelven al mismo canonical, así comparten badge. La
  // numeración se asigna ordenando los canonicals numéricamente (no por el
  // orden del array, que cambia al crear/editar) → determinista entre
  // renders.
  const pairBadgeByCanonical = useMemo(() => {
    // Paleta de colores AntD (preset names) que NO choca con los del tipo
    // de flujo (red/green/blue), para no confundir el badge del par con el
    // tag principal del movimiento.
    const PAIR_COLORS = [
      "magenta",
      "volcano",
      "orange",
      "gold",
      "lime",
      "cyan",
      "geekblue",
      "purple",
    ];
    const canonicals = new Set<string>();
    for (const f of flujos) {
      const canonical = pairKeyOf(f);
      if (canonical == null) continue;
      canonicals.add(canonical);
    }
    const sorted = Array.from(canonicals).sort((a, b) => Number(a) - Number(b));
    const map = new Map<string, { num: number; color: string }>();
    sorted.forEach((c, i) => {
      map.set(c, { num: i + 1, color: PAIR_COLORS[i % PAIR_COLORS.length] });
    });
    return map;
  }, [flujos]);

  // Resuelve el badge {num, color} de un flujo que es mitad de un par, o
  // null si no pertenece a ningún par (gasto/ingreso suelto o legacy 1-row).
  const getPairBadge = (f: Flujo) => {
    const canonical = pairKeyOf(f);
    if (canonical == null) return null;
    return pairBadgeByCanonical.get(canonical) ?? null;
  };

  // Lista de "intermediarios" representados como UNA fila por par. Para
  // los pares modernos (2 rows con paired_id), tomamos solo el lado
  // 'recibe' como representante. Los rows legacy 1-row (rol='intermediario')
  // se incluyen tal cual.
  const intermediariosRepresentativos = useMemo(() => {
    // Un representante por PAR. Se prefiere el lado 'recibe' (el que tiene el
    // origen externo en `pagadoPor`); si el par no tiene ninguno —pares legacy
    // con ambos lados 'intermediario'— se usa el de id menor, para que el
    // intermediario no desaparezca de la tab por un rol inesperado.
    const porPar = new Map<string, Flujo>();
    const sueltos: Flujo[] = [];
    for (const f of flujos) {
      const key = pairKeyOf(f);
      if (key == null) {
        if (f.rolDeAlojaFacil === "intermediario") sueltos.push(f); // legacy 1-row
        continue;
      }
      const actual = porPar.get(key);
      if (!actual) {
        porPar.set(key, f);
        continue;
      }
      const mejor =
        f.rolDeAlojaFacil === "recibe" && actual.rolDeAlojaFacil !== "recibe"
          ? f
          : actual.rolDeAlojaFacil === "recibe"
            ? actual
            : Number(f.id) < Number(actual.id)
              ? f
              : actual;
      porPar.set(key, mejor);
    }
    return [...sueltos, ...porPar.values()];
  }, [flujos]);

  // ─── Filtrado client-side por tab + rango de fecha ────────────────────
  const flujosFiltrados = useMemo(() => {
    const baseFiltrarFecha = (f: Flujo) => {
      // Filtro por fecha: solo aplica a esporádicos. Los regulares se
      // muestran siempre porque son recurrentes mensuales.
      if (f.tipo === "esporadico" && f.fecha) {
        const fechaF = dayjs(f.fecha);
        if (fechaF.isBefore(from, "day") || fechaF.isAfter(to, "day")) {
          return false;
        }
      }
      return true;
    };

    if (tab === "ingresos") {
      return flujos.filter((f) => esIngresoSuelto(f) && baseFiltrarFecha(f));
    }
    if (tab === "gastos") {
      return flujos.filter((f) => esGastoSuelto(f) && baseFiltrarFecha(f));
    }
    if (tab === "intermediarios") {
      return intermediariosRepresentativos.filter(baseFiltrarFecha);
    }
    // Tab "todos": muestra TODO (incluyendo ambas mitades de cada
    // intermediario por separado, marcadas como vinculadas).
    return flujos.filter(baseFiltrarFecha);
  }, [flujos, intermediariosRepresentativos, tab, from, to]);

  // ─── Search global (round 2026-06-02) ─────────────────────────────────
  // Filtra por: descripción, categoría, nombre de pagado_por / pagado_a,
  // monto, fecha. Normaliza sin acentos + case-insensitive — "garcia"
  // matchea "García", "10000" matchea "$ 10.000".
  const flujosFiltradosConSearch = useMemo(() => {
    const q = searchQuery.trim();
    if (!q) return flujosFiltrados;
    const norm = (s: any) =>
      String(s ?? "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "");
    const needle = norm(q);
    return flujosFiltrados.filter((f) => {
      const propNombre =
        f.propiedadId != null
          ? propiedadesById.get(f.propiedadId)?.nombre
          : null;
      // Mitad par del intermediario: la tab Intermediarios muestra como
      // "Destino (AF entrega a)" el pagadoA del ROW PAR (lado 'paga') —
      // sin incluirlo, buscar ese nombre visible daba 0 resultados
      // (auditoría 2026-07-13).
      const par =
        f.pairedId != null ? flujosById.get(Number(f.pairedId)) : null;
      const haystack = [
        f.descripcion,
        f.categoria,
        f.concepto === "reembolso_gasto" ? "reembolso" : null,
        f.pagadoPor?.nombre,
        f.pagadoA?.nombre,
        par?.pagadoPor?.nombre,
        par?.pagadoA?.nombre,
        f.monto,
        // También la forma con separador de miles que muestra la celda
        // ("10.000" encuentra $ 10.000) y la fecha como se ve (DD-MM-YYYY).
        f.monto != null ? Number(f.monto).toLocaleString("es-CL") : null,
        f.fecha,
        f.fecha ? dayjs(f.fecha).format("DD-MM-YYYY") : null,
        propNombre,
      ]
        .map(norm)
        .join(" ");
      return haystack.includes(needle);
    });
  }, [flujosFiltrados, searchQuery, propiedadesById, flujosById]);

  // ─── Orden con pares adyacentes (round 2026-06-05) ────────────────────
  //
  // En la tab "Todos" las 2 mitades de cada intermediario aparecen como
  // filas separadas. Pedido del owner: que queden ADYACENTES para leerlas
  // juntas. Ordenamos por "grupo" (ambas mitades comparten group key =
  // id del lado 'recibe'); dentro del grupo, 'recibe' (ingreso) antes que
  // 'paga' (gasto). Los grupos se ordenan por fecha desc (más reciente
  // primero). Los flujos sueltos son su propio grupo.
  //
  // Esto define el orden POR DEFECTO del dataSource; si el usuario hace
  // click en una cabecera de columna para ordenar, AntD toma el control y
  // los pares pueden separarse (comportamiento esperado). En las demás
  // tabs no hay filas pareadas, así que devolvemos la lista tal cual.
  const flujosOrdenados = useMemo(() => {
    if (tab !== "todos") return flujosFiltradosConSearch;

    const fechaVal = (f: Flujo) =>
      f.tipo === "esporadico" && f.fecha
        ? dayjs(f.fecha).valueOf()
        : Number.MAX_SAFE_INTEGER - (f.fechaPeriodica ?? 0);

    // Las dos mitades de un par comparten clave (el menor de los dos ids), así
    // quedan SIEMPRE adyacentes. Antes una mitad devolvía número y la otra
    // string, así que `ga === gb` nunca era true y el agrupamiento no ocurría:
    // que aparecieran juntas dependía de la casualidad del orden por id.
    const groupKeyOf = (f: Flujo) => pairKeyOf(f) ?? String(f.id);

    return [...flujosFiltradosConSearch].sort((a, b) => {
      const ga = groupKeyOf(a);
      const gb = groupKeyOf(b);
      if (ga === gb) {
        // Misma pareja → 'recibe' (ingreso) primero, luego 'paga' (gasto).
        const ra = a.rolDeAlojaFacil === "recibe" ? 0 : 1;
        const rb = b.rolDeAlojaFacil === "recibe" ? 0 : 1;
        return ra - rb;
      }
      // Grupos distintos → por fecha desc; desempate estable por groupKey.
      const fa = fechaVal(a);
      const fb = fechaVal(b);
      if (fa !== fb) return fb - fa;
      return Number(gb) - Number(ga);
    });
  }, [flujosFiltradosConSearch, tab]);

  // ─── KPIs del período activo ──────────────────────────────────────────
  //
  // Los KPIs siempre se calculan sobre el conjunto COMPLETO de flujos del
  // rango (no sobre el filtro de tab actual), para que cambiar de tab no
  // altere los totales globales del período.
  //
  // Reglas:
  //   • Ingresos AF      = Σ ingresos sueltos (rol='recibe', sin par).
  //   • Gastos AF        = Σ gastos sueltos (rol='paga', sin par).
  //   • Neto AF          = Ingresos − Gastos. Los pares de intermediario
  //                        no aportan (netean 0 por definición).
  //   • Intermediado     = Σ monto de cada par UNA vez (tomamos solo el
  //                        lado 'recibe' para no duplicar el conteo).
  const kpis = useMemo(() => {
    const enRango = (f: Flujo) => {
      if (f.tipo === "esporadico" && f.fecha) {
        const fechaF = dayjs(f.fecha);
        if (fechaF.isBefore(from, "day") || fechaF.isAfter(to, "day"))
          return false;
      }
      return true;
    };
    let ingresos = 0;
    let gastos = 0;
    let intermediados = 0;
    let count = 0;
    for (const f of flujos) {
      if (!enRango(f)) continue;
      count += 1;
      // Un reembolso denegado queda visible como registro, pero no es gasto.
      if (f.estadoPago === "denegado" || f.lavanderiaId != null) continue;
      // C3 (round 2026-06-20): la magnitud va al bucket; la DIRECCIÓN la
      // decide `rolDeAlojaFacil` (recibe/paga/intermediario), no el signo del
      // monto. Math.abs evita que un negativo legacy invierta un KPI. Los
      // montos nuevos ya no pueden ser negativos (C1 backend + C2 form).
      const monto = Math.abs(Number(f.monto) || 0);
      if (f.pairedId != null) {
        if (f.rolDeAlojaFacil === "recibe") intermediados += monto;
        continue;
      }
      if (f.rolDeAlojaFacil === "recibe") ingresos += monto;
      else if (f.rolDeAlojaFacil === "paga") gastos += monto;
      else if (f.rolDeAlojaFacil === "intermediario") intermediados += monto;
    }
    return {
      ingresos,
      gastos,
      neto: ingresos - gastos,
      intermediados,
      count,
    };
  }, [flujos, from, to]);

  // ─── Acciones (abrir modal, eliminar) ─────────────────────────────────
  const abrirModalCreacion = (mode: FlujoPresetMode) => {
    setModalMode(mode);
    setEditingFlujo(null);
    setEditingPair(null);
    setModalOpen(true);
  };

  const abrirModalEdicion = (flujo: Flujo) => {
    // Inferimos el modo desde el flujo. Si el row tiene `pairedId` es
    // parte de un intermediario (2 rows enlazados), por lo tanto abrimos
    // el modal en modo "intermediario". Si no, el rol del row determina
    // si fue creado como gasto o ingreso suelto.
    const mode: FlujoPresetMode =
      flujo.pairedId != null || flujo.rolDeAlojaFacil === "intermediario"
        ? "intermediario"
        : flujo.rolDeAlojaFacil === "paga"
          ? "gasto"
          : "ingreso";
    setModalMode(mode);
    setEditingFlujo(flujo);
    // Si es intermediario moderno (pairedId presente), buscamos el row
    // par en el state para que el modal pueda hidratar las dos puntas
    // externas (origen + destino) correctamente.
    if (flujo.pairedId != null) {
      const par =
        flujo.pairedId != null
          ? (flujosById.get(Number(flujo.pairedId)) ?? null)
          : null;
      setEditingPair(par);
    } else {
      setEditingPair(null);
    }
    setModalOpen(true);
  };

  const handleSaved = (saved: Flujo, pair?: FlujoPair) => {
    setModalOpen(false);
    setEditingFlujo(null);
    // Mergeo optimista en el state local para que ambos rows del par
    // queden visibles de inmediato (sin esperar el fetch). Antes
    // hacíamos fetchFlujos() y, por timing del UPDATE de cross-link en
    // backend, a veces solo aparecía un row hasta el siguiente reload.
    if (pair) {
      // Intermediario (create o update): mergeamos AMBOS rows del par.
      setFlujos((prev) => {
        const otherIds = new Set([pair.ingreso.id, pair.gasto.id]);
        const filtered = prev.filter((x) => !otherIds.has(x.id));
        return [pair.ingreso, pair.gasto, ...filtered];
      });
    } else {
      // Gasto o ingreso suelto: mergeamos el row único.
      setFlujos((prev) => {
        const idx = prev.findIndex((x) => x.id === saved.id);
        if (idx >= 0) {
          const copy = [...prev];
          copy[idx] = saved;
          return copy;
        }
        return [saved, ...prev];
      });
    }
    // Refresh defensivo en background — por si el backend hizo algo
    // adicional (ej. broadcasts a otros admins). No bloquea la UI.
    fetchFlujos();
  };

  const handleDelete = async (flujo: Flujo) => {
    // Round 2026-06-01 — flujos derivados de inventario no se borran
    // desde /flujos; admin debe ir al producto (cleanup cascadea).
    // El backend igual rechazaría con 4xx, pero damos UX clara antes.
    if (flujo.inventoryTxId != null) {
      getMsg()?.warning(
        "Este flujo viene de una compra en /inventario. Elimínalo desde el producto para que la cascada limpie todo correctamente.",
      );
      return;
    }
    try {
      // El backend devuelve los ids efectivamente borrados (el cluster
      // completo del intermediario, aunque el vínculo estuviera roto).
      // Limpiamos el state con esa lista exacta en vez de adivinar con
      // id + pairedId (que fallaba con datos viejos mal vinculados).
      const deletedIds = await eliminarFlujo(flujo.id);
      const deletedSet = new Set(deletedIds.map(String));
      // Fallback de compat: si el backend no devolvió ids, usamos el id y su
      // par declarado.
      if (deletedSet.size === 0) {
        deletedSet.add(String(flujo.id));
        if (flujo.pairedId != null) deletedSet.add(String(flujo.pairedId));
      }
      setFlujos((prev) => prev.filter((x) => !deletedSet.has(String(x.id))));
      getMsg()?.success(
        deletedSet.size > 1
          ? "Intermediario eliminado (2 movimientos)"
          : "Flujo eliminado",
      );
    } catch (err: any) {
      getMsg()?.error(err?.message ?? "Error al eliminar el flujo");
    }
  };

  // ─── Columnas de la tabla ─────────────────────────────────────────────
  //
  // En la tab Intermediarios mostramos un único row representante (lado
  // 'recibe' del par), con la columna 'Pagado a' renderizando el destino
  // del row par (lado 'paga'). Eso es lo que el usuario espera ver: un
  // intermediario como UNA fila que muestra origen → AF → destino.
  //
  // En la tab Todos, los 2 rows del par aparecen separados con un badge
  // discreto 🔗 que indica que están enlazados.
  const columns = useMemo(
    () => [
      {
        title: "Tipo",
        key: "tipo",
        // Round 2026-06-02 — ancho aumentado de 140 a 200 + flexWrap en
        // el Space para que los badges 🔗 (paired) y 📦 (producto) no
        // queden cortados cuando coexisten con el tag principal
        // "🔁 Intermediario" (que ya es ancho).
        width: 200,
        sorter: (a: Flujo, b: Flujo) =>
          (a.rolDeAlojaFacil || "").localeCompare(b.rolDeAlojaFacil || ""),
        render: (_: any, f: Flujo) => {
          // En la tab Intermediarios, mostramos "Intermediario" como Tag
          // principal + el badge #N del par (para cruzar referencias con la
          // tab Todos, donde el par aparece como 2 filas).
          if (tab === "intermediarios") {
            const badge = getPairBadge(f);
            return (
              <Space size={4} wrap>
                <Tag color="blue" style={{ marginRight: 0 }}>
                  🔁 Intermediario
                </Tag>
                {badge && (
                  <Tooltip title={`Par #${badge.num}`}>
                    <Tag color={badge.color} style={{ marginRight: 0 }}>
                      <LinkOutlined /> #{badge.num}
                    </Tag>
                  </Tooltip>
                )}
              </Space>
            );
          }
          const meta = rolToTipoFlujo(f.rolDeAlojaFacil);
          const esPar = f.pairedId != null;
          const pairBadge = getPairBadge(f);
          const esProducto = f.inventoryTxId != null;
          return (
            <Space size={4} wrap>
              <Tag
                color={
                  f.rolDeAlojaFacil === "paga"
                    ? "red"
                    : f.rolDeAlojaFacil === "recibe"
                      ? "green"
                      : "blue"
                }
                style={{ marginRight: 0 }}
              >
                {meta.icon} {meta.label}
              </Tag>
              {esPar && (
                <Tooltip
                  title={`Intermediario — par #${pairBadge?.num ?? ""}. Las dos mitades (el ingreso y el gasto) comparten este número y color: misma etiqueta = mismo par. Pasa el mouse para resaltarlas; editar o eliminar afecta a ambas.`}
                >
                  <Tag
                    color={pairBadge?.color ?? "purple"}
                    style={{ marginRight: 0, padding: "0 6px" }}
                  >
                    <LinkOutlined /> #{pairBadge?.num ?? ""}
                  </Tag>
                </Tooltip>
              )}
              {esProducto && (
                <Tooltip title="Generado automáticamente desde una compra de inventario. Para cambiar el monto edita el producto en /inventario; eliminarlo desde /inventario también borra este flujo.">
                  <Tag
                    color="orange"
                    style={{ marginRight: 0, padding: "0 6px" }}
                  >
                    📦
                  </Tag>
                </Tooltip>
              )}
              {f.lavanderiaId != null && <Tooltip title="Reembolso de un gasto registrado en lavandería; no se suma otra vez a los gastos de la empresa."><Tag color="purple">Lavandería</Tag></Tooltip>}
            </Space>
          );
        },
      },
      {
        title: "Frecuencia",
        key: "frecuencia",
        width: 150,
        sorter: (a: Flujo, b: Flujo) => (a.tipo || "").localeCompare(b.tipo || ""),
        render: (_: any, f: Flujo) => {
          const m = frecuenciaToBadge(f.tipo);
          if (f.tipo !== "regular") return <Tag color={m.color}>{m.label}</Tag>;
          // Vigencia del regular (round 2026-07-26): el gasto solo rige dentro
          // de su rango, así que el estado importa tanto como la frecuencia.
          const est = estadoVigencia(f);
          const meta = ESTADO_VIGENCIA_TAG[est];
          const desde = f.vigenciaInicio ? dayjs(f.vigenciaInicio).format("MM/YYYY") : null;
          const hasta = f.vigenciaFin ? dayjs(f.vigenciaFin).format("MM/YYYY") : null;
          const detalle =
            est === "sin_vigencia"
              ? "Este gasto regular no tiene definido desde cuándo rige, así que cuenta en todos los meses. Edítalo para acotarlo."
              : `Rige ${desde ? `desde ${desde}` : ""}${hasta ? ` hasta ${hasta}` : " sin término definido"}.`;
          return (
            <Tooltip title={detalle}>
              <div style={{ lineHeight: 1.3 }}>
                <Tag color={m.color}>{m.label}</Tag>
                <div>
                  <Tag color={meta.color} style={{ marginTop: 2 }}>
                    {meta.texto}
                  </Tag>
                </div>
              </div>
            </Tooltip>
          );
        },
      },
      {
        title: "Fecha",
        key: "fecha",
        width: 130,
        // Sorter: esporádicos por fecha ISO real; regulares por su día
        // del mes ofsetteado (1-28) para no romper la comparación. El
        // criterio: esporádicos siempre antes que regulares cuando se
        // ordena ascendente (los regulares son recurrentes — no tienen
        // fecha "real" comparable con un esporádico).
        sorter: (a: Flujo, b: Flujo) => {
          const av =
            a.tipo === "esporadico" && a.fecha
              ? dayjs(a.fecha).valueOf()
              : Number.MAX_SAFE_INTEGER - (a.fechaPeriodica ?? 0);
          const bv =
            b.tipo === "esporadico" && b.fecha
              ? dayjs(b.fecha).valueOf()
              : Number.MAX_SAFE_INTEGER - (b.fechaPeriodica ?? 0);
          return av - bv;
        },
        render: (_: any, f: Flujo) =>
          f.tipo === "regular"
            ? `Día ${f.fechaPeriodica ?? "—"} de c/mes`
            : f.fecha
              ? dayjs(f.fecha).format("DD-MM-YYYY")
              : "—",
      },
      {
        title: "Monto",
        key: "monto",
        width: 140,
        align: "right" as const,
        sorter: (a: Flujo, b: Flujo) => (Number(a.monto) || 0) - (Number(b.monto) || 0),
        render: (_: any, f: Flujo) => (
          <span
            style={{
              fontWeight: 600,
              color:
                f.rolDeAlojaFacil === "paga"
                  ? "#cf1322"
                  : f.rolDeAlojaFacil === "recibe"
                    ? "#389e0d"
                    : "#1677ff",
            }}
          >
            {clp(f.monto)}
          </span>
        ),
      },
      {
        title: tab === "intermediarios" ? "Origen (paga a AF)" : "Pagado por",
        key: "pagadoPor",
        ellipsis: true,
        sorter: (a: Flujo, b: Flujo) =>
          (a.pagadoPor?.nombre || "").localeCompare(b.pagadoPor?.nombre || ""),
        render: (_: any, f: Flujo) => {
          // En la tab Intermediarios, el representante es el lado
          // 'recibe', así que su `pagadoPor` ya es el origen externo.
          // No hace falta buscar el par para esta columna.
          return renderContraparte(f.pagadoPor, f.propiedadId, propiedadesById);
        },
      },
      {
        title: tab === "intermediarios" ? "Destino (AF entrega a)" : "Pagado a",
        key: "pagadoA",
        ellipsis: true,
        sorter: (a: Flujo, b: Flujo) =>
          (a.pagadoA?.nombre || "").localeCompare(b.pagadoA?.nombre || ""),
        render: (_: any, f: Flujo) => {
          // En la tab Intermediarios mostramos el destino REAL: si f es
          // el lado 'recibe' (con pairedId), su pagadoA es AF — el
          // destino externo vive en el row par (lado 'paga').
          if (tab === "intermediarios" && f.pairedId != null) {
            const par = flujosById.get(Number(f.pairedId));
            return renderContraparte(
              par?.pagadoA ?? null,
              par?.propiedadId ?? f.propiedadId,
              propiedadesById,
            );
          }
          return renderContraparte(f.pagadoA, f.propiedadId, propiedadesById);
        },
      },
      {
        title: "Categoría",
        key: "categoria",
        width: 120,
        sorter: (a: Flujo, b: Flujo) =>
          (a.categoria || "").localeCompare(b.categoria || ""),
        render: (_: any, f: Flujo) => (
          <>
            <Tag>
              {f.categoria === "sueldo" && f.concepto === "reembolso_gasto"
                ? "Reembolso"
                : f.categoria
                ? (CATEGORIA_LABEL[f.categoria] ?? f.categoria)
                : "—"}
            </Tag>
            {f.concepto === "reembolso_gasto" && f.estadoPago === "denegado" && <Tag color="red">Denegado</Tag>}
          </>
        ),
      },
      {
        title: "Descripción",
        key: "descripcion",
        ellipsis: true,
        sorter: (a: Flujo, b: Flujo) =>
          (a.descripcion || "").localeCompare(b.descripcion || ""),
        render: (_: any, f: Flujo) => (
          <Tooltip title={f.descripcion}>
            <span>{f.descripcion ?? "—"}</span>
          </Tooltip>
        ),
      },
      {
        title: "Adjuntos",
        key: "adjuntos",
        width: 155,
        render: (_: any, f: Flujo) => (
          <AdjuntosEnFila flujo={f} par={f.pairedId != null ? flujosById.get(Number(f.pairedId)) : null} onChanged={fetchFlujos} />
        ),
      },
      {
        title: "Acciones",
        key: "acciones",
        width: 110,
        render: (_: any, f: Flujo) => (
          <Space>
            <Tooltip
              title={
                f.pairedId != null
                  ? "Editar intermediario (afecta a los 2 movimientos vinculados)"
                  : "Editar"
              }
            >
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => abrirModalEdicion(f)}
              />
            </Tooltip>
            <Popconfirm
              title={
                f.pairedId != null
                  ? "¿Eliminar este intermediario?"
                  : "¿Eliminar este flujo?"
              }
              description={
                f.pairedId != null
                  ? "Se borrarán los 2 movimientos vinculados (el ingreso y el gasto del intermediario). No se puede deshacer."
                  : "No se puede deshacer."
              }
              okText="Sí, eliminar"
              cancelText="Cancelar"
              onConfirm={() => handleDelete(f)}
            >
              <Tooltip title="Eliminar">
                <Button size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    // `tab`, `flujosById` y `propiedadesById` cambian el render de
    // algunas columnas (Tipo y Pagado a en la tab Intermediarios;
    // Pagado por/a cuando la contraparte es propietario tipo=1).
    // `abrirModalEdicion` y `handleDelete` son closures sobre el
    // state — están en deps implícitamente por la closure, pero por
    // linter las omitimos a propósito (referencias estables dentro
    // del componente).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tab, flujosById, propiedadesById, pairBadgeByCanonical, fetchFlujos],
  );

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="home-content"
    >
      <div style={{ padding: 16 }}>
        <PageHeader
          title="Flujos de caja AF Empresa"
          helpText="Movimientos de dinero relacionados con Aloja Fácil Empresa: gastos (AF paga), ingresos (AF cobra) e intermediaciones (AF entrega y recibe el mismo monto). Los aseos, sueldos, lavandería y arriendos tienen sus propios módulos — este es para los demás flujos operativos."
        />

        {/* Banner explicativo. */}
        <Alert
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 16 }}
          message="Cómo funcionan los flujos de caja"
          description={
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>
              <p style={{ marginTop: 0, marginBottom: 6 }}>
                Cada movimiento se categoriza por su efecto sobre la caja
                de AF Empresa. Usa los <strong>3 botones de creación</strong>{" "}
                arriba para no equivocarte:
              </p>
              <ul style={{ marginTop: 0, marginBottom: 6, paddingLeft: 20 }}>
                <li>
                  <Tag color="red">💳 Gasto</Tag> AF Empresa{" "}
                  <strong>paga</strong>. Eliges sólo a quién (cliente,
                  arrendatario, aseador, trabajador o receptor externo).
                </li>
                <li>
                  <Tag color="green">💵 Ingreso</Tag> AF Empresa{" "}
                  <strong>cobra</strong>. Eliges sólo quién paga.
                </li>
                <li>
                  <Tag color="blue">🔁 Intermediario</Tag> AF Empresa{" "}
                  <strong>recibe y luego paga</strong> el mismo monto.
                  Crea automáticamente <strong>2 movimientos vinculados</strong>{" "}
                  (un ingreso y un gasto): editarlos o eliminarlos cascadea
                  al par. Ninguna punta puede ser AF (está en el medio del
                  flujo). Neto AF = 0.
                </li>
              </ul>
              <p style={{ margin: 0, fontSize: 12, color: "#6b7280" }}>
                <strong>Frecuencia</strong>: esporádico = pago puntual con
                fecha; regular = se repite mensualmente el día N (1–28).
              </p>
            </div>
          }
        />

        {/* Selector de fecha: mes / rango / mes anterior. Mismo componente
            que /finanzas-empresa para coherencia visual. */}
        <div style={{ marginBottom: 16 }}>
          <FiltroGlobalFecha value={filtroFecha} onChange={setFiltroFecha} />
        </div>

        {/* Toolbar: 3 botones de creación. */}
        <Space wrap style={{ marginBottom: 16 }}>
          <Button
            type="primary"
            danger
            icon={<PlusOutlined />}
            onClick={() => abrirModalCreacion("gasto")}
          >
            💳 Nuevo gasto
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ background: "#389e0d", borderColor: "#389e0d" }}
            onClick={() => abrirModalCreacion("ingreso")}
          >
            💵 Nuevo ingreso
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => abrirModalCreacion("intermediario")}
          >
            🔁 Nuevo intermediario
          </Button>
        </Space>

        {/* KPIs del período activo. */}
        <Card
          size="small"
          style={{ marginBottom: 16 }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <Space size="large" wrap>
            <KpiPill
              label="Ingresos AF"
              value={kpis.ingresos}
              color="#389e0d"
              tooltip="Suma de flujos sueltos donde AF cobra (sin contar las mitades de intermediarios — esas se autocancelan)."
            />
            <KpiPill
              label="Gastos AF"
              value={kpis.gastos}
              color="#cf1322"
              tooltip="Suma de flujos sueltos donde AF paga (sin contar las mitades de intermediarios — esas se autocancelan)."
            />
            <KpiPill
              label="Neto AF"
              value={kpis.neto}
              color={kpis.neto >= 0 ? "#389e0d" : "#cf1322"}
              tooltip="Neto AF del módulo Flujos = Ingresos − Gastos. Los intermediarios netean 0 por definición y no se incluyen."
              showSign
            />
            <KpiPill
              label="Intermediado (referencial)"
              value={kpis.intermediados}
              color="#1677ff"
              tooltip="Suma de pares de intermediario (un monto por par, no la duplicación). Sirve para auditar el volumen que pasa por AF, pero NO afecta el Neto AF."
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              <strong>{kpis.count}</strong>{" "}
              {kpis.count === 1 ? "flujo" : "flujos"} en el período
            </Typography.Text>
          </Space>
        </Card>

        <IntermediacionesLavanderia desde={from.format("YYYY-MM-DD")} hasta={to.format("YYYY-MM-DD")} />

        {/* Tabs por tipo */}
        <Card
          tabList={[
            { key: "todos", tab: `Todos (${flujos.length})` },
            {
              key: "ingresos",
              tab: `💵 Ingresos (${flujos.filter(esIngresoSuelto).length})`,
            },
            {
              key: "gastos",
              tab: `💳 Gastos (${flujos.filter(esGastoSuelto).length})`,
            },
            {
              key: "intermediarios",
              tab: `🔁 Intermediarios (${intermediariosRepresentativos.length})`,
            },
          ]}
          activeTabKey={tab}
          onTabChange={(k) => setTab(k as TabKey)}
        >
          {/* Round 2026-06-02 — search global + hint de hover paired */}
          <div
            style={{
              marginBottom: 12,
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Input
              allowClear
              placeholder="Buscar por descripción, categoría, contraparte, propiedad o monto..."
              prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ maxWidth: 480, flex: "1 1 280px" }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {searchQuery.trim()
                ? `${flujosFiltradosConSearch.length} resultado${flujosFiltradosConSearch.length === 1 ? "" : "s"}`
                : "Cada intermediario tiene un nº y color (🔗 #N): sus 2 mitades lo comparten. Pasa el mouse para resaltar el par."}
            </Typography.Text>
          </div>

          {/* Cola de revisión de la vigencia (round 2026-07-26). Los gastos
              regulares creados antes de este round no tienen definido desde
              cuándo rigen y no hay de dónde inferirlo (la tabla no guarda
              fecha de creación), así que el sistema NO se la inventa: siguen
              contando en todos los meses, igual que antes, y quedan acá
              listados para que un humano los acote cuando quiera. */}
          {(() => {
            const sinVigencia = flujos.filter(
              (f) => f.tipo === "regular" && !f.vigenciaInicio && !f.vigenciaFin,
            );
            if (sinVigencia.length === 0) return null;
            return (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 12 }}
                message={`${sinVigencia.length} gasto${sinVigencia.length === 1 ? "" : "s"} regular${sinVigencia.length === 1 ? "" : "es"} sin vigencia registrada`}
                description={
                  <>
                    Se crearon antes de que existiera la vigencia, así que{" "}
                    <strong>siguen contando en todos los meses</strong>. Ábrelos y
                    define desde qué mes rigen (y hasta cuándo, si corresponde)
                    para que dejen de sumar donde no deben:{" "}
                    {sinVigencia
                      .slice(0, 6)
                      .map((f) => f.nombre || f.descripcion || `#${f.id}`)
                      .join(" · ")}
                    {sinVigencia.length > 6 ? ` … y ${sinVigencia.length - 6} más` : ""}
                    .
                  </>
                }
              />
            );
          })()}

          <Table
            size="small"
            rowKey={(r) => r.id}
            dataSource={flujosOrdenados}
            columns={columns as any}
            loading={loading}
            pagination={paginacionCliente({ porDefecto: 20, etiqueta: "flujo" })}
            // Round 2026-06-02 — fila con highlight si es la fila hovered
            // o su par (paired_id ↔ id). Color suave amber para no romper
            // la jerarquía visual.
            // El ancla es la CLAVE DEL PAR, no el id de la fila: así las dos
            // mitades se iluminan juntas. Antes se guardaba el id (número) y
            // se comparaba contra `String(pairedId)`, así que nunca coincidían
            // y solo se pintaba la fila bajo el cursor.
            rowClassName={(record) => {
              if (!hoveredPairAnchor) return "";
              return pairKeyOf(record) === hoveredPairAnchor
                ? "flujos-row-paired-hover"
                : "";
            }}
            onRow={(record) => ({
              onMouseEnter: () => {
                // Solo activamos el highlight cuando el flujo es un par.
                const key = pairKeyOf(record);
                if (key != null) setHoveredPairAnchor(key);
              },
              onMouseLeave: () => {
                if (hoveredPairAnchor === pairKeyOf(record)) {
                  setHoveredPairAnchor(null);
                }
              },
            })}
            locale={{
              emptyText: (
                <Empty
                  description={
                    searchQuery.trim()
                      ? "Sin resultados para la búsqueda"
                      : tab === "todos"
                        ? "Sin flujos en el período seleccionado"
                        : `Sin flujos de tipo ${tab} en el período`
                  }
                />
              ),
            }}
          />
        </Card>
      </div>

      {/* Round 2026-06-02 — CSS para el highlight de las filas paired al
          hover. Se inyecta con un <style> tag scoped por la className
          .flujos-row-paired-hover. No usamos modules CSS para no agregar
          un archivo por una sola regla. */}
      <style>{`
        .flujos-row-paired-hover > td {
          background-color: #fef3c7 !important;
          transition: background-color 0.15s ease;
        }
      `}</style>

      {/* Modal de creación / edición */}
      <FlujoFormModal
        open={modalOpen}
        mode={modalMode}
        editing={editingFlujo}
        editingPair={editingPair}
        usuarios={usuarios}
        propiedades={propiedades}
        onCancel={() => {
          setModalOpen(false);
          setEditingFlujo(null);
          setEditingPair(null);
        }}
        onSaved={handleSaved}
      />
    </Layout>
  );
}

// ── Sub-componente KPI compacto ───────────────────────────────────────────

function KpiPill({
  label,
  value,
  color,
  tooltip,
  showSign,
}: {
  label: string;
  value: number;
  color: string;
  tooltip: string;
  showSign?: boolean;
}) {
  return (
    <Tooltip title={tooltip}>
      <div style={{ display: "inline-flex", flexDirection: "column" }}>
        <span style={{ fontSize: 11, color: "#6b7280" }}>{label}</span>
        <span style={{ fontSize: 18, fontWeight: 700, color }}>
          {showSign && value > 0 ? "+" : ""}
          {clp(value)}
        </span>
      </div>
    </Tooltip>
  );
}
